<template>
  <footer class="site-footer">
    <div class="site-footer__main">
      <div class="container">
        <div class="site-footer__grid">

          <!-- Brand column -->
          <div class="site-footer__brand">
            <router-link to="/" class="site-footer__logo">
              <img src="@/assets/img/home/logo-footer.png" alt="SherlockIT" />
            </router-link>
            <p class="site-footer__desc">{{ tagline }}</p>
            <ul class="site-footer__delivers">
              <li v-for="item in delivers" :key="item"><i class="fa fa-check"></i> {{ item }}</li>
            </ul>
            <div class="site-footer__social">
              <a v-for="s in social_links" :key="s.platform"
                 :href="s.url" target="_blank" rel="noopener"
                 class="site-footer__social-link" :aria-label="s.platform">
                <i :class="s.icon"></i>
              </a>
            </div>
          </div>

          <!-- Dynamic nav columns from CMS -->
          <div v-for="col in nav_columns" :key="col.heading" class="site-footer__col">
            <div class="site-footer__col-title">{{ col.heading }}</div>
            <ul class="site-footer__links">
              <li v-for="link in col.links" :key="link.label">
                <router-link v-if="link.url && link.url.startsWith('/')" :to="link.url">{{ link.label }}</router-link>
                <a v-else :href="link.url" target="_blank" rel="noopener">{{ link.label }}</a>
              </li>
            </ul>
          </div>

        </div>
      </div>
    </div>

    <!-- Bottom bar -->
    <div class="site-footer__bottom">
      <div class="container">
        <div class="site-footer__bottom-inner">
          <span>{{ copyrightText }}</span>
          <span class="site-footer__badge">
            <i class="fa fa-shield"></i> {{ badge_text }}
          </span>
        </div>
      </div>
    </div>
  </footer>
</template>

<script>
import { cmsFooter } from "@/services/cms";

const DEFAULTS = {
  tagline: "Next-generation, real-time corporate intelligence for cybersecurity, compliance & supply-chain risk. SherlockIT empowers organisations with high-integrity business data, explainable AI and agentic workflows.",
  delivers: [
    "Real-time, source-verified corporate intelligence from trusted registries",
    "Traceable, auditable data with full provenance",
    "Explainable AI for automated risk discovery and monitoring",
    "Agentic system that learns, verifies, and improves continuously",
    "Actionable insights for cybersecurity, supply chain, fintech, regtech & compliance",
  ],
  nav_columns: [
    { heading: "Platform", links: [
      { label: "General Search", url: "/search" },
      { label: "Direct Search",  url: "/direct-search" },
      { label: "Insights",       url: "/insights" },
      { label: "FAQ",            url: "/faq" },
    ]},
    { heading: "Company", links: [
      { label: "Home",               url: "/" },
      { label: "Company",            url: "/about-us" },
      { label: "Contact Us",         url: "/contact-us" },
      { label: "Log In",             url: "/login" },
      { label: "Terms & Conditions", url: "/terms-and-conditions" },
    ]},
  ],
  social_links: [
    { platform: "Facebook",  icon: "fa fa-facebook",     url: "https://www.facebook.com/Sherlockit-1455719934547305/" },
    { platform: "Twitter",   icon: "fa fa-twitter",      url: "https://twitter.com/intent/user?screen_name=ItSherlockit" },
    { platform: "LinkedIn",  icon: "fa fa-linkedin",     url: "https://www.linkedin.com/company/sherlockit/" },
    { platform: "YouTube",   icon: "fa fa-youtube-play", url: "https://www.youtube.com/watch?v=9uAPPsKwq2o&feature=youtu.be" },
  ],
  copyright:  "Copyright © 2016 – {year} SherlockIT. All rights reserved.",
  badge_text: "Court-admissible chain of custody · Arabic & multilingual NLP",
};

export default {
  name: "app-footer",
  data() {
    return {
      year: new Date().getFullYear(),
      ...DEFAULTS,
    };
  },
  computed: {
    copyrightText() {
      return this.copyright.replace('{year}', this.year);
    },
  },
  async mounted() {
    const applyFooter = (cms) => {
      if (cms.tagline)      this.tagline      = cms.tagline;
      if (cms.badge_text)   this.badge_text   = cms.badge_text;
      if (cms.copyright)    this.copyright    = cms.copyright;
      if (cms.delivers?.length)    this.delivers    = cms.delivers;
      if (cms.nav_columns?.length) this.nav_columns = cms.nav_columns;
      if (cms.social_links?.length)this.social_links = cms.social_links;
    };
    const cms = await cmsFooter(DEFAULTS, applyFooter.bind(this));
    applyFooter(cms);
  },
};
</script>

<style scoped>
.site-footer {
  background: #fff;
  border-top: 1px solid #e2e8f0;
  font-family: inherit;
}

.container { max-width: 1200px; margin: 0 auto; padding: 0 24px; }

/* Main grid */
.site-footer__main { padding: 56px 0 48px; }
.site-footer__grid {
  display: grid;
  grid-template-columns: 1.8fr 1fr 1fr;
  gap: 48px;
}

/* Brand */
.site-footer__logo { display: inline-block; margin-bottom: 20px; }
.site-footer__logo img { height: 36px; width: auto; }
.site-footer__desc {
  font-size: 13px;
  color: #64748b;
  line-height: 1.7;
  max-width: 400px;
  margin-bottom: 20px;
}
.site-footer__delivers {
  list-style: none;
  padding: 0;
  margin: 0 0 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.site-footer__delivers li {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 12px;
  color: #64748b;
  line-height: 1.6;
}
.site-footer__delivers li i {
  color: #575ECF;
  font-size: 11px;
  margin-top: 3px;
  flex-shrink: 0;
}

/* Social */
.site-footer__social { display: flex; align-items: center; gap: 8px; }
.site-footer__social-link {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  text-decoration: none;
  transition: border-color 0.15s, color 0.15s, background 0.15s;
}
.site-footer__social-link:hover {
  border-color: #575ECF;
  color: #575ECF;
  background: rgba(87,94,207,0.05);
  text-decoration: none;
}

/* Nav columns */
.site-footer__col-title {
  font-size: 12px;
  font-weight: 600;
  color: #0a1428;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 16px;
}
.site-footer__links {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.site-footer__links a {
  font-size: 13px;
  color: #64748b;
  text-decoration: none;
  transition: color 0.15s;
}
.site-footer__links a:hover { color: #575ECF; text-decoration: none; }

/* Bottom bar */
.site-footer__bottom {
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
  padding: 16px 0;
}
.site-footer__bottom-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 12px;
  color: #94a3b8;
}
.site-footer__badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #94a3b8;
}
.site-footer__badge i { color: #575ECF; }

/* Responsive */
@media (max-width: 768px) {
  .site-footer__grid { grid-template-columns: 1fr 1fr; gap: 32px; }
  .site-footer__brand { grid-column: 1 / -1; }
  .site-footer__bottom-inner { flex-direction: column; align-items: flex-start; gap: 8px; }
}
@media (max-width: 480px) {
  .site-footer__grid { grid-template-columns: 1fr; }
}
</style>
